import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetAvailableSourceTablesQueryKey,
  usePostSourceTables,
} from '@/api/control/control-plane/control-plane';
import { useRoles } from '@/contexts/RolesContext';
import { useError } from '@/contexts/ErrorContext';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { logger } from '@/services/logger';
import {
  SourceTablePicker,
  type SelectedTableConfig,
} from '@/components/profiles/sources/SourceTablePicker';
import { useOrgs } from '@/contexts/OrgsContext';
import { TableBatchFailureAlert } from '@/components/profiles/sources/TableBatchFailureAlert';
import { runWithConcurrency } from '@/lib/runWithConcurrency';
import { exceedsDataverseCapacity, fillTemplate } from '@/lib/sourceCapacity';
import { useDataverseConcurrencyLimit } from '@/hooks/useDataverseConcurrencyLimit';
import {
  buildAddSourceTableRequest,
  retainSelection,
  SOURCE_TABLE_CREATE_CONCURRENCY,
  type FailedTable,
} from '@/lib/sourceTableBatch';

/**
 * Outcome of the most recent partially failed batch, shown until the next attempt.
 */
interface BatchFailureState {
  addedCount: number;
  totalCount: number;
  failures: FailedTable[];
}

/**
 * Props for the CreateSourceTableDialog component
 */
interface CreateSourceTableDialogProps {
  /** The organization ID */
  orgId: string;
  /** The profile ID */
  profileId: string;
  /** The source ID */
  sourceId: string;
  /** The source type discriminator ($type from SourceDto), used to build the correct polymorphic request. */
  sourceType: string;
  /**
   * The target's optional table prefix. Passed to the picker so tables whose prefixed staging
   * name would exceed the 128-char SQL identifier limit are disabled.
   */
  tablePrefix?: string;
  /**
   * Optional per-table hook fired immediately after each table is created, within the same
   * concurrency slot. Used by callers that want to drive post-create work like triggering an
   * initial sync. A failure here does not fail the table (it was created); it is counted and
   * reported once the batch finishes.
   */
  onTableCreated?: (sourceTableId: string) => Promise<void> | void;
  /** Callback to trigger after tables are successfully created */
  onTablesCreated?: () => void;
  /** Optional external control of dialog open state */
  externalOpen?: boolean;
  /** Optional callback when external open state should change */
  onExternalOpenChange?: (open: boolean) => void;
}

/**
 * Dialog for adding source tables to an existing profile source.
 * Handles Dataverse, Business Central, and Salesforce sources by routing
 * the correct polymorphic $type to the add-source-table endpoint.
 */
export function CreateSourceTableDialog({ orgId, profileId, sourceId, sourceType, tablePrefix, onTableCreated, onTablesCreated, externalOpen, onExternalOpenChange }: CreateSourceTableDialogProps) {
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const isOpen = externalOpen ?? internalOpen;
  const setIsOpen = onExternalOpenChange ?? setInternalOpen;
  const [selectedTables, setSelectedTables] = useState<Map<string, SelectedTableConfig>>(new Map());
  const [validationError, setValidationError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { mutateAsync: createSourceTable } = usePostSourceTables();
  // Tracked locally rather than via the mutation's isPending, which only reflects the most recent
  // of many concurrent calls and would re-enable the button while the batch is still running.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [batchFailure, setBatchFailure] = useState<BatchFailureState | null>(null);
  const isCreating = progress !== null;

  // Server-driven per-org capability: whether the "select all" control is offered (issue #451).
  // Resolved server-side from the Auth org feature `sync.allowAllTables` and returned on the org
  // settings DTO. Read via a localized cast until the control API client is regenerated to include
  // the field (run `npm run gen:api:control` against a live backend).
  const dataverseConcurrencyLimit = useDataverseConcurrencyLimit();
  const { orgSettings } = useOrgs();
  const canSelectAllTables = (orgSettings as { allowAllTables?: boolean } | null)?.allowAllTables ?? false;

  /**
   * Handles the create action. Creates the selected tables with bounded concurrency so a large
   * "select all" batch does not flood the API, and lets every table run even when some fail. On a
   * partial failure the dialog stays open with only the failed tables still selected, so a retry
   * never re-submits tables that were already added.
   */
  const handleCreate = async () => {
    setValidationError(null);
    setBatchFailure(null);

    if (selectedTables.size === 0) {
      setValidationError(t.sources.sourceTablesDialog.selectAtLeastOne);
      return;
    }

    const entries = Array.from(selectedTables.entries());
    let syncStartFailures = 0;
    setProgress({ done: 0, total: entries.length });

    const outcomes = await runWithConcurrency(
      entries,
      SOURCE_TABLE_CREATE_CONCURRENCY,
      async ([logicalName, config]) => {
        const requestData = buildAddSourceTableRequest(logicalName, config, sourceType);
        logger.debug('CreateSourceTableDialog.createSourceTable', { logicalName, requestData });
        const response = await createSourceTable({ orgId, profileId, sourceId, data: requestData });

        if (onTableCreated && response.data.sourceTableId) {
          try {
            await onTableCreated(response.data.sourceTableId);
          } catch (error) {
            // The table exists; only its post-create work failed. Never mark it as failed, or a
            // retry would re-submit it and be rejected as a duplicate.
            syncStartFailures++;
            logger.error(error as Error, { context: 'CreateSourceTableDialog.onTableCreated', logicalName });
          }
        }
      },
      (done, total) => { setProgress({ done, total }); },
    );

    const failures: FailedTable[] = [];
    for (const outcome of outcomes) {
      if (!outcome.ok) {
        const { message } = handleApiError(
          outcome.error,
          { context: 'CreateSourceTableDialog.createSourceTable' },
          { showToast: false },
        );
        failures.push({ logicalName: outcome.item[0], message });
      }
    }
    const addedCount = entries.length - failures.length;

    logger.debug('CreateSourceTableDialog.batchComplete', {
      tableCount: entries.length,
      addedCount,
      failedCount: failures.length,
      syncStartFailures,
    });
    setProgress(null);

    // The server excludes configured tables from the available list; refetch so tables added in
    // this batch drop out of the picker.
    void queryClient.invalidateQueries({
      queryKey: getGetAvailableSourceTablesQueryKey(orgId, profileId, sourceId),
    });

    if (addedCount > 0 && onTablesCreated) {
      onTablesCreated();
    }

    if (syncStartFailures > 0) {
      toast.warning(t.sources.tableBatch.syncStartFailed.replace('{count}', String(syncStartFailures)));
    }

    if (failures.length > 0) {
      setSelectedTables(retainSelection(selectedTables, failures.map((f) => f.logicalName)));
      setBatchFailure({ addedCount, totalCount: entries.length, failures });
      return;
    }

    setIsOpen(false);
    setSelectedTables(new Map());
    toast.success(t.sources.sourceTablesDialog.addSuccess);
  };

  /**
   * Handles dialog close and resets form state. Closing is blocked while a batch is running so
   * its progress and outcome are not lost.
   */
  const handleClose = (open: boolean) => {
    if (!open && isCreating) return;
    setIsOpen(open);
    if (!open) {
      setSelectedTables(new Map());
      setValidationError(null);
      setBatchFailure(null);
    }
  };

  const isControlled = externalOpen !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {isAdminOrOperator && !isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            {t.sources.sourceTablesDialog.buttonText}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t.sources.sourceTablesDialog.title}</DialogTitle>
          <DialogDescription>
            {t.sources.sourceTablesDialog.description}
          </DialogDescription>
        </DialogHeader>
        {/* Scrolls when the picker (paste results + fixed-height list) is taller than the space
            left in the dialog. Without this the list overflows onto the footer and its rows,
            being positioned, sit above the Add button and swallow its clicks. */}
        <div className="flex flex-col gap-4 py-4 flex-1 min-h-0 overflow-y-auto">
          <SourceTablePicker
            orgId={orgId}
            profileId={profileId}
            sourceId={sourceId}
            tablePrefix={tablePrefix}
            enabled={isOpen}
            value={selectedTables}
            onChange={setSelectedTables}
            scrollAreaClassName="h-[400px]"
            selectAllEnabled={canSelectAllTables}
            strings={{
              tablesLabel: t.sources.sourceTablesDialog.tables,
              selectedSuffix: t.sources.sourceTablesDialog.selected,
              selectAllLabel: t.sources.sourceTablesDialog.selectAll,
              deselectAllLabel: t.sources.sourceTablesDialog.deselectAll,
              searchPlaceholder: t.sources.sourceTablesDialog.searchPlaceholder,
              loadingMessage: t.sources.sourceTablesDialog.loading,
              noTablesFound: t.sources.sourceTablesDialog.noTables,
              noTablesAvailable: t.sources.sourceTablesDialog.noTables,
              timestampFieldLabel: t.sources.sourceTablesDialog.timestampField,
              timestampFieldPlaceholder: t.sources.sourceTablesDialog.timestampFieldDescription,
              timestampFieldNone: t.sources.sourceTablesDialog.timestampFieldNone,
              incrementalSyncVia: t.sources.sourceTablesDialog.incrementalSyncVia,
              fullReplication: t.sources.sourceTablesDialog.fullReplication,
              noTimestampFieldNotice: t.sources.sourceTablesDialog.noTimestampFieldNotice,
              nameTooLongForPrefix: t.sources.sourceTablesDialog.nameTooLongForPrefix,
            }}
          />
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {exceedsDataverseCapacity(sourceType, dataverseConcurrencyLimit, selectedTables.size) && (
            <p className="text-sm text-muted-foreground">
              {fillTemplate(t.sources.sourceCapacity.selectionHint, {
                limit: dataverseConcurrencyLimit,
                count: selectedTables.size,
              })}
            </p>
          )}
          {batchFailure && (
            <TableBatchFailureAlert
              addedCount={batchFailure.addedCount}
              totalCount={batchFailure.totalCount}
              failures={batchFailure.failures}
            />
          )}
          {validationError && (
            <Alert className="background-error border-error">
              <AlertCircle className="h-4 w-4 text-error" />
              <AlertDescription className="text-error">{validationError}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => { handleClose(false); }} disabled={isCreating}>
              {t.sources.sourceTablesDialog.cancel}
            </Button>
            <Button onClick={() => { void handleCreate(); }} disabled={isCreating || selectedTables.size === 0}>
              {progress
                ? t.sources.tableBatch.progress
                    .replace('{done}', String(progress.done))
                    .replace('{total}', String(progress.total))
                : `${t.sources.sourceTablesDialog.addTables} ${String(selectedTables.size)} ${selectedTables.size !== 1 ? t.sources.sourceTablesDialog.tables : 'Table'}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
